import { useNavigate } from "react-router-dom";
import { Plus, ExternalLink, BarChart3, TrendingUp, Pencil, Target, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLeaderboards } from "@/hooks/use-leaderboards";
import { useAuth } from "@/contexts/auth-context";

const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];

const formatDateRange = (start: string, end: string) => {
  const s = new Date(start);
  const e = new Date(end);
  const sy = `'${String(s.getFullYear()).slice(2)}`;
  const ey = `'${String(e.getFullYear()).slice(2)}`;
  const sMonth = monthNames[s.getMonth()];
  const eMonth = monthNames[e.getMonth()];

  if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear() && s.getDate() === 1) {
    const lastDay = new Date(e.getFullYear(), e.getMonth() + 1, 0).getDate();
    if (e.getDate() === lastDay) return `The whole of ${sMonth} ${sy}`;
    return `${s.getDate()} – ${e.getDate()} ${sMonth} ${sy}`;
  }
  if (s.getFullYear() === e.getFullYear()) {
    return `${s.getDate()} ${sMonth} – ${e.getDate()} ${eMonth} ${sy}`;
  }
  return `${s.getDate()} ${sMonth} ${sy} – ${e.getDate()} ${eMonth} ${ey}`;
};

const statusColors: Record<string, string> = {
  Live: "bg-accent/20 text-accent",
  Scheduled: "bg-primary/10 text-primary",
  Ended: "bg-muted text-muted-foreground",
};

const CampaignList = () => {
  const navigate = useNavigate();
  const { effectiveVenueId } = useAuth();
  const { data: campaigns = [], isLoading, error } = useLeaderboards(effectiveVenueId);

  const totalUplift = campaigns.reduce((sum, c) => sum + c.roiUplift, 0);
  const avgRoi = campaigns.filter((c) => c.roiPercent > 0);
  const avgRoiPercent = avgRoi.length > 0 ? Math.round(avgRoi.reduce((s, c) => s + c.roiPercent, 0) / avgRoi.length) : 0;
  const activeCampaigns = campaigns.filter((c) => c.status === "Live").length;
  const subscriptionCost = campaigns.length * 40;
  const netRoi = totalUplift - subscriptionCost;

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-destructive">Failed to load campaigns. Please try again.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Campaigns</h1>
          <p className="text-sm text-muted-foreground">Manage your leaderboard campaigns</p>
        </div>
        <Button onClick={() => navigate("/app/campaigns/new")}>
          <Plus className="mr-2 h-4 w-4" /> Create Campaign
        </Button>
      </div>

      {/* ROI Headline Banner */}
      <div className="mt-6 rounded-2xl border-2 border-accent/20 bg-gradient-to-r from-accent/5 via-card to-primary/5 p-6">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="h-5 w-5 text-accent" />
          <h2 className="font-bold text-lg">Your Leaderboards ROI</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <p className="text-sm text-muted-foreground">Revenue uplift from campaigns</p>
            <p className="text-3xl font-extrabold text-accent">+${totalUplift.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Leaderboards subscription cost</p>
            <p className="text-3xl font-extrabold text-muted-foreground">-${subscriptionCost.toLocaleString()}<span className="text-base font-normal">/mo</span></p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Net ROI</p>
            <p className="text-3xl font-extrabold text-foreground">+${netRoi.toLocaleString()}</p>
            <p className="text-xs text-accent font-semibold mt-0.5">
              {avgRoiPercent > 0 ? `${Math.round(netRoi / subscriptionCost)}x return on investment` : "Calculating…"}
            </p>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-2">
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <TrendingUp className="h-4 w-4 text-primary" />
            Avg Campaign ROI
          </div>
          <p className="mt-1 text-2xl font-bold">{avgRoiPercent}%</p>
          <p className="mt-0.5 text-xs text-muted-foreground">Revenue lift vs baseline</p>
        </div>
        <div className="rounded-xl border bg-card p-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Target className="h-4 w-4 text-primary" />
            Active Now
          </div>
          <p className="mt-1 text-2xl font-bold">{activeCampaigns}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{campaigns.length} total campaigns</p>
        </div>
      </div>

      {/* Desktop table */}
      <div className="mt-6 hidden lg:block rounded-xl border bg-card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b text-left text-sm text-muted-foreground">
              <th className="px-4 py-3 font-medium">Campaign</th>
              <th className="px-4 py-3 font-medium">Date Range</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Metric</th>
              <th className="px-4 py-3 font-medium">Est. ROI</th>
              <th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  No campaigns yet.{" "}
                  <button
                    onClick={() => navigate("/app/campaigns/new")}
                    className="text-primary underline hover:opacity-80"
                  >
                    Create your first campaign
                  </button>
                </td>
              </tr>
            ) : (
              campaigns.map((c) => (
              <tr key={c.id} className="border-b last:border-0 hover:bg-muted/50 transition-colors">
                <td className="px-4 py-4 font-medium">{c.name}</td>
                <td className="px-4 py-4 text-sm text-muted-foreground whitespace-nowrap">{formatDateRange(c.startDate, c.endDate)}</td>
                <td className="px-4 py-4">
                  <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusColors[c.status]}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-muted-foreground">{c.metric}</td>
                <td className="px-4 py-4">
                  {c.roiUplift > 0 ? (
                    <button
                      onClick={() => navigate(`/app/campaigns/${c.id}/roi`)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1 text-sm font-semibold text-accent hover:bg-accent/20 transition-colors whitespace-nowrap"
                    >
                      <TrendingUp className="h-3.5 w-3.5" />
                      +${c.roiUplift.toLocaleString()}
                    </button>
                  ) : (
                    <span className="text-sm text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/display/${c.id}`)} title="Open TV Display">
                      <ExternalLink className="h-4 w-4" /> Preview
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/app/campaigns/${c.id}/report`)}>
                      <BarChart3 className="h-4 w-4" /> Report
                    </Button>
                    {c.status === "Ended" && (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/app/campaigns/${c.id}/management-report`)}>
                        <FileText className="h-4 w-4" /> 🏁 Mgmt Report
                      </Button>
                    )}
                    {c.status !== "Ended" && (
                      <Button size="sm" variant="ghost" onClick={() => navigate(`/app/campaigns/${c.id}/edit`)}>
                        <Pencil className="h-4 w-4" /> Edit
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="mt-8 space-y-3 lg:hidden">
        {campaigns.length === 0 ? (
          <div className="rounded-xl border bg-card p-4 text-center text-muted-foreground py-12">
            No campaigns yet.{" "}
            <button
              onClick={() => navigate("/app/campaigns/new")}
              className="text-primary underline hover:opacity-80"
            >
              Create your first campaign
            </button>
          </div>
        ) : (
          campaigns.map((c) => (
          <div key={c.id} className="rounded-xl border bg-card p-4 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium truncate">{c.name}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{formatDateRange(c.startDate, c.endDate)}</p>
              </div>
              <span className={`shrink-0 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${statusColors[c.status]}`}>
                {c.status}
              </span>
            </div>
            <div className="flex flex-wrap gap-2 border-t pt-3">
              <Button size="sm" variant="ghost" className="flex-1" onClick={() => navigate(`/display/${c.id}`)}>
                <ExternalLink className="mr-1 h-3.5 w-3.5" /> Preview
              </Button>
              <Button size="sm" variant="ghost" className="flex-1" onClick={() => navigate(`/app/campaigns/${c.id}/report`)}>
                <BarChart3 className="mr-1 h-3.5 w-3.5" /> Report
              </Button>
              {c.status !== "Ended" && (
                <Button size="sm" variant="ghost" className="flex-1" onClick={() => navigate(`/app/campaigns/${c.id}/edit`)}>
                  <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                </Button>
              )}
            </div>
          </div>
          ))
        )}
      </div>
    </div>
  );
};

export default CampaignList;
